import React, { useEffect, useState } from "react";

import { Warehouse } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { WAREHOUSE_ADDRESS } from "~/constants/warehouse";
import { supabase } from "~/integrations/supabase/client";
import { Address, SalesRequest } from "~/types/client";
import { isPickupOrder } from "~/utils/addressUtils";

interface DeliveryInfoProps {
  salesRequest: SalesRequest;
  className?: string;
  variant?: "full" | "short";
}

const DeliveryInfo: React.FC<DeliveryInfoProps> = ({
  salesRequest,
  className = "",
  variant = "full",
}) => {
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null);
  const [loading, setLoading] = useState(false);
  const isPickup = isPickupOrder(salesRequest);

  // Fetch the actual delivery address data
  useEffect(() => {
    const fetchDeliveryAddress = async () => {
      if (isPickup || !salesRequest.deliveryAddressId) {
        setDeliveryAddress(null);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("addresses")
          .select("*")
          .eq("id", salesRequest.deliveryAddressId)
          .single();

        if (error) throw error;

        // Transform database response to match Address interface
        const transformedAddress: Address = {
          id: data.id,
          clientId: data.client_id,
          street: data.street,
          number: data.number,
          complement: data.complement,
          city: data.city,
          state: data.state,
          comuna: data.comuna,
          isDefault: data.is_default,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        };

        setDeliveryAddress(transformedAddress);
      } catch (error) {
        console.error("Error fetching delivery address:", error);
        setDeliveryAddress(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryAddress();
  }, [salesRequest.deliveryAddressId, isPickup]);

  const short = variant === "short";

  // Split the address into a main line (street) and a secondary muted line
  const getAddressLines = (): { main: string; secondary: string } => {
    if (isPickup) {
      return {
        main: short
          ? WAREHOUSE_ADDRESS.name
          : `${WAREHOUSE_ADDRESS.name} - ${WAREHOUSE_ADDRESS.street}`,
        secondary: short ? "" : WAREHOUSE_ADDRESS.comuna,
      };
    }

    // Prefer the fetched address; fall back to denormalized delivery fields
    const source = deliveryAddress ?? {
      street: salesRequest.deliveryStreet,
      number: salesRequest.deliveryNumber,
      complement: salesRequest.deliveryComplement,
      comuna: salesRequest.deliveryComuna,
      city: salesRequest.deliveryCity,
    };

    const secondaryParts = short
      ? [source.city]
      : [source.complement, source.comuna, source.city];

    return {
      main: [source.street, source.number].filter(Boolean).join(" "),
      secondary: secondaryParts.filter(Boolean).join(", "),
    };
  };

  const { main, secondary } = getAddressLines();

  if (loading) {
    return (
      <p className={`text-sm text-muted-foreground ${className}`}>
        Cargando dirección...
      </p>
    );
  }

  if (!main && !secondary) {
    return (
      <p className={`text-sm text-muted-foreground ${className}`}>
        Sin dirección de entrega
      </p>
    );
  }

  return (
    <div className={`space-y-1 text-sm ${className}`}>
      {isPickup && (
        <Badge variant="secondary" className="gap-1">
          <Warehouse className="h-3 w-3" />
          Cliente retira
        </Badge>
      )}
      <div>
        {main && <p className="font-semibold">{main}</p>}
        {secondary && (
          <p className="text-xs text-muted-foreground">{secondary}</p>
        )}
      </div>
    </div>
  );
};

export default DeliveryInfo;
